import "./quillSetup"
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import ReactQuill from "react-quill"
import Backdrop from "@mui/material/Backdrop"
import CircularProgress from "@mui/material/CircularProgress"
import { subirImagenContenido } from "../../services/blog"

const toolbar = [
    [{ 'header': [1, 2, false] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
    ['link', 'image', 'video'],
    ['clean']
]

const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image', 'video',
    'table'
]

const TIPOS_IMAGEN = "image/png,image/jpeg,image/webp,image/gif"

const soloImagenes = (lista?: FileList | null) =>
    Array.from(lista || []).filter(f => f.type.startsWith("image/"))

interface Props {
    value?: string,
    onChange?: ReactQuill.ReactQuillProps["onChange"],
    carpeta?: string,
}

const CustomTextArea: FC<Props> = ({ value, onChange, carpeta = "Blog" }) => {

    const quillRef = useRef<ReactQuill>(null)
    // Contador, no booleano: puede haber varias subidas en paralelo (pegar o
    // soltar varias imagenes de golpe) y el loader se va con la ultima.
    const [subidasEnCurso, setSubidasEnCurso] = useState(0)

    const subir = useCallback(async (file: File) => {
        setSubidasEnCurso(n => n + 1)
        try {
            return await subirImagenContenido(file, carpeta)
        } finally {
            setSubidasEnCurso(n => n - 1)
        }
    }, [carpeta])

    const avisarError = (error: any) => {
        console.error("Error al subir imagen:", error)
        alert(error?.message || "No se pudo subir la imagen")
    }

    // En serie, para que queden insertadas en el mismo orden en que venian y
    // no en el orden en que responda el servidor.
    const subirEInsertar = useCallback(async (files: File[]) => {
        const quill = quillRef.current?.getEditor()
        if (!quill) return

        for (const file of files) {
            try {
                const url = await subir(file)
                const range = quill.getSelection(true)
                quill.insertEmbed(range.index, "image", url, "user")
                quill.setSelection(range.index + 1, 0, "user")
            } catch (error) {
                avisarError(error)
            }
        }
    }, [subir])

    // Pegado de HTML (Word, Docs): las imagenes vienen como base64 dentro del
    // markup, asi que se reemplazan una vez insertadas.
    const reemplazarBase64 = useCallback(async () => {
        const quill = quillRef.current?.getEditor()
        if (!quill) return

        // El flag evita que dos barridos seguidos suban dos veces lo mismo.
        const imgs = Array.from(
            quill.root.querySelectorAll<HTMLImageElement>('img[src^="data:image/"]:not([data-subiendo])')
        )
        if (!imgs.length) return
        imgs.forEach(img => img.setAttribute("data-subiendo", "1"))

        for (const img of imgs) {
            try {
                const blob = await (await fetch(img.src)).blob()
                const ext = (blob.type.split("/")[1] || "png").replace("jpeg", "jpg")
                const url = await subir(new File([blob], `pegada.${ext}`, { type: blob.type }))
                img.setAttribute("src", url)
                img.removeAttribute("data-subiendo")
            } catch (error) {
                img.remove()
                avisarError(error)
            }
        }
        // El src se cambio por fuera de Quill: hay que resincronizar el delta.
        quill.update()
    }, [subir])

    // Boton "imagen" del toolbar: en vez del prompt de URL de Quill, abre un
    // file picker y sube el archivo.
    const imageHandler = useCallback(() => {
        const input = document.createElement("input")
        input.type = "file"
        input.accept = TIPOS_IMAGEN
        input.multiple = true
        input.onchange = () => subirEInsertar(soloImagenes(input.files))
        input.click()
    }, [subirEInsertar])

    // useMemo obligatorio: si el objeto modules cambia de referencia,
    // react-quill reinstancia el editor en cada render.
    const modules = useMemo(() => ({
        toolbar: {
            container: toolbar,
            handlers: { image: imageHandler },
        },
        table: true,
    }), [imageHandler])

    useEffect(() => {
        const quill = quillRef.current?.getEditor()
        if (!quill) return

        // Los listeners van en el contenedor y en fase de captura: Quill ya
        // tiene los suyos en quill.root y se registraron antes, asi que es la
        // unica forma de cortarlos antes de que peguen la imagen en base64
        // (si no, sale duplicada).
        const onPaste = (event: ClipboardEvent) => {
            const files = soloImagenes(event.clipboardData?.files)

            // Imagen pegada como archivo (captura de pantalla, copiar imagen).
            if (files.length) {
                event.preventDefault()
                event.stopPropagation()
                subirEInsertar(files)
                return
            }

            // 50ms y no 0: Quill inserta el HTML pegado dentro de un
            // setTimeout(1) propio, asi que a 0 el barrido corre antes de que
            // las imagenes existan en el DOM.
            setTimeout(reemplazarBase64, 50)
        }

        const onDrop = (event: DragEvent) => {
            const files = soloImagenes(event.dataTransfer?.files)
            if (!files.length) return

            event.preventDefault()
            event.stopPropagation()

            // Deja el cursor donde se solto el archivo; si el navegador no
            // expone la API (Firefox), cae al final del contenido.
            const range = (document as any).caretRangeFromPoint?.(event.clientX, event.clientY)
            if (range) {
                const seleccion = window.getSelection()
                seleccion?.removeAllRanges()
                seleccion?.addRange(range)
            }

            subirEInsertar(files)
        }

        // `container` existe en runtime pero no en los tipos de Quill 1.x
        const contenedor: HTMLElement = (quill as any).container
        contenedor.addEventListener("paste", onPaste as EventListener, true)
        contenedor.addEventListener("drop", onDrop as EventListener, true)

        return () => {
            contenedor.removeEventListener("paste", onPaste as EventListener, true)
            contenedor.removeEventListener("drop", onDrop as EventListener, true)
        }
    }, [subirEInsertar, reemplazarBase64])

    return (
        <div style={{ position: "relative" }}>
            <ReactQuill ref={quillRef}
                value={value}
                theme="snow"
                modules={modules}
                formats={formats}
                onChange={onChange} />

            <Backdrop open={subidasEnCurso > 0} sx={{ position: "absolute", zIndex: 10, color: "#fff", flexDirection: "column" }}>
                <CircularProgress color="inherit" />
                <div style={{ marginTop: 8, fontSize: 14 }}>
                    Subiendo {subidasEnCurso} {subidasEnCurso === 1 ? "imagen" : "imágenes"}...
                </div>
            </Backdrop>
        </div>
    )
}

export default CustomTextArea
